import { CheckIcon, CloseIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import {
  Button,
  Flex,
  HStack,
  IconButton,
  Input,
  Table, Tbody,
  Td,
  Th, Thead, Tr,
  VStack,
  useColorModeValue,
  useToast,
  Link as ChakraLink
} from '@chakra-ui/react';
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import OperacoesController from "controllers/Operacoes/OperacoesController";
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

function OperationsView() {
  const [data, setData] = useState([]);
  const [newCampanhaNome, setNewCampanhaNome] = useState("");
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();

  useEffect(() => {
    const fetchOperations = async () => {
      try {
        const response = await OperacoesController.ListOperations();
        setData(response);
      } catch (error) {
        console.error("Error fetching operations:", error);
        toast({
          title: "Erro ao buscar operações.",
          description: "Não foi possível buscar as operações. Tente novamente mais tarde.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };
    fetchOperations();
  }, [toast]);

  const addCampanha = async () => {
    if (!newCampanhaNome.trim()) {
      toast({
        title: "Nome inválido.",
        description: "Por favor, insira um nome válido.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await OperacoesController.AddOperations(newCampanhaNome);
      if (response) {
        const updatedCampanha = await OperacoesController.ListOperations();
        setData(updatedCampanha);
        setNewCampanhaNome("");
        toast({
          title: "Operação adicionada.",
          description: "A nova operação foi adicionada com sucesso.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error adding campanha:", error);
      toast({
        title: "Erro ao adicionar operação.",
        description: "Não foi possível adicionar a operação. Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex direction='column' height={"100%"} width="100%">
      <Card overflowX={{ sm: "scroll", xl: "hidden" }}>
        <CardHeader>
          <VStack spacing={2} align="stretch">
            <HStack spacing={2}>
              <Input
                value={newCampanhaNome}
                onChange={(e) => setNewCampanhaNome(e.target.value)}
                placeholder="Nome"
              />
              <Button onClick={addCampanha} colorScheme="blue" width="auto" minWidth="200px">
                Adicionar
              </Button>
            </HStack>
          </VStack>
        </CardHeader>
        <CardBody>
          <Table variant='simple' color={textColor}>
            <Thead>
              <Tr color='gray.400'>
                <Th color={textColor}>ID</Th>
                <Th color={textColor}>OPERAÇÃO</Th>
                <Th color={textColor}>AÇÕES</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.map((campanha) => (
                <CampanhaRow
                  key={campanha.id}
                  campanha={campanha}
                  setData={setData}
                />
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>
    </Flex>
  );
}

function CampanhaRow({ campanha, setData }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNome, setEditedNome] = useState(campanha.description);
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      editCampanha();
    }
  }

  const editCampanha = async () => {
    if (!editedNome.trim()) {
      toast({
        title: "Nome inválido.",
        description: "Por favor, insira um nome válido.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const response = await OperacoesController.PutOperation(campanha.id, editedNome);
      if (response) {
        const updatedCampanha = await OperacoesController.ListOperations();
        setData(updatedCampanha);
        setIsEditing(false);
        toast({
          title: "Operação atualizada.",
          description: "A operação foi atualizada com sucesso.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error editing campanha:", error);
      toast({
        title: "Erro ao editar operação.",
        description: "Não foi possível editar a operação. Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const deleteCampanha = async () => {
    try {
      const response = await OperacoesController.DeleteOperations(campanha.id);
      if (response) {
        const updatedCampanha = await OperacoesController.ListOperations();
        setData(updatedCampanha);
        toast({
          title: "Operação deletada.",
          description: "A operação foi removida com sucesso.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting campanha:", error);
      toast({
        title: "Erro ao deletar operação.",
        description: "Não foi possível deletar a operação. Tente novamente mais tarde.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Tr>
      <Td>{campanha.id}</Td>
      <Td>
        {isEditing ? (
          <Input
            value={editedNome}
            onChange={e => setEditedNome(e.target.value)}
            onKeyPress={handleKeyPress}
            onBlur={() => {
              editCampanha();
              setIsEditing(false);
            }} 
          />
        ) : (
          <ChakraLink
            as={Link}
            to={`/admin/campanhas/${campanha.description}/${campanha.id}`}
            color="blue.500"
            _hover={{ textDecoration: 'underline' }}
          >
            {campanha.description}
          </ChakraLink>
        )}
      </Td>
      <Td>
        {isEditing ? (
          <>
            <IconButton icon={<CheckIcon />} onClick={editCampanha} mr={2} />
            <IconButton icon={<CloseIcon />} onClick={() => setIsEditing(false)} mr={2} />
          </>
        ) : (
          <>
            <IconButton icon={<EditIcon />} onClick={() => setIsEditing(true)} mr={2} />
            <IconButton icon={<DeleteIcon />} onClick={deleteCampanha} mr={2} />
          </>
        )}
      </Td>
    </Tr>
  );
}

export default OperationsView;